import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Eye, Clock, MoreHorizontal,
  Edit, Trash2, Pin, Flag
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const PostCard = ({ post, onLike, onComment, onDelete, onEdit }) => {
  const navigate = useNavigate();
  const profile = useSelector(state => state.profile);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLiked, setIsLiked] = useState(post.isLikedByUser);
  const [likeCount, setLikeCount] = useState(post.likeCount || 0);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const postDate = new Date(date);
    const diffInHours = Math.floor((now - postDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'now';
    if (diffInHours < 24) return `${diffInHours}h`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d`;
    return postDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/home/forum/posts/${post._id}`);
       toast.dismiss() ;  toast.success('Post link copied to clipboard');
      setShowDropdown(false);
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleLike = async () => {
    try {
      const response = await onLike();
      if (response) {
        setIsLiked(response.isLiked);
        setLikeCount(response.likeCount);
      }
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  // THESE ARE THE KEY FUNCTIONS FOR EDIT/DELETE
  const handleEdit = () => {
    console.log('Edit clicked for post:', post._id); // Debug log
    setShowDropdown(false);
    if (onEdit) {
      onEdit(post);
    }
  };

  const handleDelete = () => {
    console.log('Delete clicked for post:', post._id); // Debug log
    setShowDropdown(false);
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      if (onDelete) {
        onDelete(post._id);
      }
    }
  };

  const isAuthor = post.alumniId._id === profile._id;
  const isAdmin = profile.admin;

  return (
    <motion.article
      whileHover={{ y: -1 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
    >
      <div className="p-3 lg:p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
            {post.alumniId.profilePicture ? (
              <img
                src={post.alumniId.profilePicture}
                alt={`${post.alumniId.firstName} ${post.alumniId.lastName}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full dynamic-site-bg flex items-center justify-center text-white font-medium text-sm">
                {post.alumniId.firstName?.[0]}{post.alumniId.lastName?.[0]}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-sm lg:text-base text-gray-900 truncate">
                {post.alumniId.firstName} {post.alumniId.lastName}
              </h3>
              {post.alumniId.graduation_year && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0">
                  '{post.alumniId.graduation_year.toString().slice(-2)}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock size={12} />
              <span>{formatTimeAgo(post.createdAt)}</span>
              <span>•</span>
              <span className="dynamic-site-bg text-white px-2 py-0.5 rounded-full">
                {post.categoryId.name}
              </span>
            </div>
          </div>

          {/* DROPDOWN MENU - THIS IS CRUCIAL */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
            >
              <MoreHorizontal size={16} className="text-gray-500" />
            </button>

            {showDropdown && (
              <>
                {/* <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)}></div> */}
                <div className="absolute right-0 top-8 w-40 bg-white rounded-lg shadow-lg border py-1 z-20">
                  
                  {/* AUTHOR CAN EDIT/DELETE */}
                  {isAuthor && (
                    <>
                      <button 
                        onClick={handleEdit}
                        className="w-full cursor-pointer flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      <button 
                        onClick={handleDelete}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </>
                  )}
                  
                  {/* ADMIN CAN DELETE (NOT AUTHOR) */}
                  {isAdmin && !isAuthor && (
                    <>
                      
                      <button 
                        onClick={handleDelete}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </>
                  )}

                  {/* SHARE FOR EVERYONE */}
                  <button 
                    onClick={handleShare}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Share2 size={14} />
                    Share
                  </button>

               
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="mb-3">
          <h2
            onClick={() => navigate(`/home/forum/posts/${post._id}`)}
            className="font-bold text-base lg:text-lg text-gray-900 mb-2 hover:text-blue-600 cursor-pointer line-clamp-2 leading-tight"
          >
            {post.title}
          </h2>
          
          <div
            className="text-gray-700 text-sm line-clamp-3 cursor-pointer leading-relaxed"
            onClick={() => navigate(`/home/forum/posts/${post._id}`)}
          >
            {post.content}
          </div>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {post.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 cursor-pointer"
              >
                #{tag}
              </span>
            ))}
            {post.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-50 text-gray-500 rounded-full text-xs">
                +{post.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Eye size={14} />
              {post.views || 0}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle size={14} />
              {post.commentCount || 0}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium transition-colors ${
                isLiked
                  ? 'bg-red-50 text-red-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Heart size={14} className={isLiked ? 'fill-current' : ''} />
              <span>{likeCount}</span>
            </button>

            <button
              onClick={() => navigate(`/home/forum/posts/${post._id}`)}
              className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
            >
              <MessageCircle size={14} />
              <span className="hidden sm:inline">Reply</span>
            </button>

            <button
              onClick={handleShare}
              className="p-1 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Share2 size={14} />
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default PostCard;
